import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import env from '@jwp/ott-common/src/env';
import classNames from 'classnames';
import { EDIT_BROADCAST_LIVE, EditBroadcastLiveData, EditBroadcastLiveInput, EditBroadcastLiveVariables } from '@jwp/ott-common/src/graphql/mutation/broadcastLive.mutation';
import { GET_S3_UPLOAD_PRESIGNED_URL, GetS3UploadPresignedUrlData } from '@jwp/ott-common/src/graphql/query/reels.queries';
import {
  GET_DROPDOWNS, GetDropdownsData, GetDropdownsVariables, DropdownType, GET_BROADCAST_LIVE,
  GetBroadcastLiveData,
  GetBroadcastLiveVariables,
  BroadcastLiveStatus
} from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { generateS3Key } from '@jwp/ott-common/src/utils/s3';
import {
  getMaxBroadcastEventEndTime,
  validateBroadcastEventScheduleEdit,
  validateBroadcastEventTimeSchedule,
  validateBroadcastEventDateSchedule,
} from '@jwp/ott-common/src/utils/broadcastLive';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { PATH_BROADCAST_LIVE_DETAILS } from '@jwp/ott-common/src/paths';
import CalendarIcon from '@jwp/ott-theme/assets/icons/Calendar.svg?react';
import ClockIcon from '@jwp/ott-theme/assets/icons/ClockIcon.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import HelpIcon from '@jwp/ott-theme/assets/icons/Help.svg?react';

import TextField from '../TextField/TextField';
import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';
import Icon from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import Calendar from '../Calendar/Calendar';
import UploadBox from '../UploadBox/UploadBox';

import PickerField from './PickerField';
import styles from './CreateLiveStreamModal.module.scss';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

const THUMBNAIL_SIZE_LIMIT_MB = 1;

// Statuses where NOTHING on the form can be edited (all fields + Save button disabled)
const FULLY_LOCKED_STATUSES = [
  BroadcastLiveStatus.creating,
  BroadcastLiveStatus.capturing,
  BroadcastLiveStatus.captured,
  BroadcastLiveStatus.deleting,
  BroadcastLiveStatus.destroying,
  BroadcastLiveStatus.failed,
  BroadcastLiveStatus.requested,
  BroadcastLiveStatus.starting,
  BroadcastLiveStatus.stopped,
  BroadcastLiveStatus.stopping,
];

type DropdownOption = { value: string; label: string };

const useDropdownOptions = (ddType: DropdownType): DropdownOption[] => {
  const { data } = useQuery<GetDropdownsData, GetDropdownsVariables>(GET_DROPDOWNS, {
    client: getAuthApolloClient(),
    variables: { ddType },
    errorPolicy: 'all',
  });
  return useMemo(() => (data?.getDropdowns?.dropdowns || []).map((item) => ({ value: item.ddValue, label: item.ddLabel })), [data]);
};

const generateTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const h24 = Math.floor(minutes / 60);
    const m = minutes % 60;
    const period = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    options.push(`${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`);
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Normalize a free-text time entry to hh:mm AM/PM format.
// Returns null if the text cannot be parsed.
const normalizeTimeInput = (raw: string): string | null => {
  const trimmed = raw.trim().toUpperCase();

  const match = /^(\d{1,2})(?::?(\d{1,2}))?\s*(AM|PM)?$/.exec(trimmed);

  if (!match) { return null; }

  let hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2] || '0', 10);
  let period = match[3];

  if (Number.isNaN(hours) || Number.isNaN(minutes)) { return null; }
  if (hours > 23) { return null; }
  if (minutes < 0 || minutes > 59) { return null; }

  if (period && (hours < 1 || hours > 12)) {
    return null;
  }

  if (!period) {
    period = hours < 12 ? 'AM' : 'PM';
  }

  if (match[3]) {
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (period === 'AM' && hours === 12) {
      hours = 0;
    }
  }

  const h12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${String(h12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
};

const formatTimeLabel = (date: Date): string => {
  const h24 = date.getHours();
  const m = date.getMinutes();
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
};

const formatDateDisplay = (date: Date | null): string => {
  if (!date) { return ''; }
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${date.getFullYear()}`;
};

const parseTimeLabel = (label: string): { hours: number; minutes: number } | null => {
  const match = /^(\d{1,2}):(\d{2})\s(AM|PM)$/.exec(label);
  if (!match) { return null; }
  let hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  const period = match[3];
  if (period === 'PM' && hours !== 12) { hours += 12; }
  if (period === 'AM' && hours === 12) { hours = 0; }
  return { hours, minutes };
};

const combineDateTime = (date: Date | null, timeLabel: string): Date | null => {
  if (!date || !timeLabel) { return null; }
  const time = parseTimeLabel(timeLabel);
  if (!time) { return null; }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.hours, time.minutes, 0, 0);
};

const toIsoUtc = (date: Date | null): string | null => {
  if (!date) { return null; }
  return `${date.toISOString().split('.')[0]}Z`;
};

const formatUtcDisplay = (date: Date | null): string => {
  if (!date) { return ''; }
  const iso = date.toISOString();
  const [datePart, timePart] = iso.split('T');
  return `${datePart}, ${timePart.split('.')[0]}`;
};

const TimeList: React.FC<{ value: string; onSelect: (value: string) => void }> = ({ value, onSelect }) => (
  <ul className={styles.timeList} role="listbox">
    {TIME_OPTIONS.map((option) => (
      <li
        key={option}
        role="option"
        aria-selected={option === value}
        tabIndex={0}
        className={classNames(styles.timeOption, { [styles.timeOptionSelected]: option === value })}
        onClick={() => onSelect(option)}
        onKeyDown={(e) => e.key === 'Enter' && onSelect(option)}
      >
        {option}
      </li>
    ))}
  </ul>
);

const EditLiveStreamModal: React.FC = () => {
  const { t } = useTranslation(['video', 'upload', 'common']);
  const navigate = useNavigate();
  const user = useAccountStore((state) => state.user);
  const location = useLocation();
  const streamId = location.pathname.split('/').filter(Boolean).pop();

  const [streamType, setStreamType] = useState<'event' | '247'>('event');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [ingestRegion, setIngestRegion] = useState('');
  const [ingestFormat, setIngestFormat] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState('');
  const [contentType, setContentType] = useState('');
  const [dvrWindow, setDvrWindow] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  const { data: broadcastData } = useQuery<GetBroadcastLiveData, GetBroadcastLiveVariables>(GET_BROADCAST_LIVE, {
    client: getAuthApolloClient(),
    variables: { blId: streamId || '' },
    skip: !streamId,
    errorPolicy: 'all',
  });
  const [getPresignedUrl] = useLazyQuery<GetS3UploadPresignedUrlData, { key: string }>(GET_S3_UPLOAD_PRESIGNED_URL);

  const regionOptions = useDropdownOptions('ingest_region');
  const formatOptions = useDropdownOptions('ingest_format');
  const contentTypeOptions = useDropdownOptions('content_type');
  const dvrOptions = useDropdownOptions('dvr_window');

  // Preselect the first available option for the non-required dropdowns once loaded
  useEffect(() => {
    if (!contentType && contentTypeOptions.length > 0) {
      setContentType(contentTypeOptions[0].value);
    }
  }, [contentType, contentTypeOptions]);

  useEffect(() => {
    if (!dvrWindow && dvrOptions.length > 0) {
      setDvrWindow(dvrOptions[0].value);
    }
  }, [dvrWindow, dvrOptions]);

  const stream = broadcastData?.getBroadcastLive?.broadcastLive;
  const streamStatus = stream?.blStatus as BroadcastLiveStatus;

  // Whether NOTHING on the form can be edited (creating/capturing/captured/deleting/
  // destroying/failed/requested/starting/stopped/stopping)
  const isFullyLocked = FULLY_LOCKED_STATUSES.includes(streamStatus);

  // Status-based edit permissions for schedule fields specifically
  const canEditStartTime = !isFullyLocked && [BroadcastLiveStatus.ready, BroadcastLiveStatus.scheduled].includes(streamStatus);

  const canEditEndTime = !isFullyLocked && [
    BroadcastLiveStatus.idle,
    BroadcastLiveStatus.ready,
    BroadcastLiveStatus.scheduled,
    BroadcastLiveStatus.streaming,
  ].includes(streamStatus);

  const startDateTime = useMemo(() => combineDateTime(startDate, startTime), [startDate, startTime]);
  const endDateTime = useMemo(() => combineDateTime(endDate, endTime), [endDate, endTime]);

  const scheduleError = useMemo(() => {
    if (!startDateTime || !endDateTime) { return null; }
    const err = validateBroadcastEventScheduleEdit(startDateTime, endDateTime);
    // Start field is locked for this status (e.g. Idle/Streaming) — the user
    // can't touch it, so this rule must not block saving End Date/Time changes.
    if (err === 'start_too_soon' && !canEditStartTime) { return null; }
    return err;
  }, [startDateTime, endDateTime, canEditStartTime]);

  const scheduleDateError = useMemo(() => {
    if (!startDateTime || !endDateTime) { return null; }
    const err = validateBroadcastEventDateSchedule(startDateTime, endDateTime);
    if (err === 'start_too_soon' && !canEditStartTime) { return null; }
    return err;
  }, [startDateTime, endDateTime, canEditStartTime]);


  const scheduleTimeError = useMemo(() => {
    if (!startDateTime || !endDateTime) { return null; }
    const err = validateBroadcastEventTimeSchedule(startDateTime, endDateTime);
    if (err === 'start_too_soon' && !canEditStartTime) { return null; }
    return err;
  }, [startDateTime, endDateTime, canEditStartTime]);


  const maxEndDateTime = useMemo(
    () => (startDateTime ? getMaxBroadcastEventEndTime(startDateTime) : null),
    [startDateTime],
  );


  useEffect(() => {
    if (stream) {
      setTitle(stream.blTitle || '');
      setDescription(stream.blDescription || '');
      setIngestRegion(stream.blIngestRegion || '');
      setIngestFormat(stream.blIngestFormat || '');
      setDvrWindow(String(stream.blDvrWindow || ''));
      if (stream.blStartTime) {
        const sDate = new Date(stream.blStartTime);
        setStartDate(sDate);
        setStartTime(formatTimeLabel(sDate));
        setStartTimeError('');
      }
      if (stream.blEndTime) {
        const eDate = new Date(stream.blEndTime);
        setEndDate(eDate);
        setEndTime(formatTimeLabel(eDate));
        setEndTimeError('');
      }
      if (stream.blStreamType === 'event' || stream.blStreamType === '247') {
        setStreamType(stream.blStreamType);
      }
      if (stream.blThumbnailUrl) {
        setThumbnailPreview(`${env.APP_BASE_URL || ''}${stream.blThumbnailUrl}`);
      }
    }
  }, [stream]);

  const getScheduleErrorMessage = (error: ReturnType<typeof validateBroadcastEventScheduleEdit>, time?: string): string => {
    switch (error) {
      case 'end_before_start':
        return t('video:broadcast_live.end_after_start');
      case 'min_duration':
        return t('video:broadcast_live.min_duration');
      case 'max_duration':
        return t('video:broadcast_live.max_duration', time == 'time' ? {
          value: startTime,
          defaultValue: `Date may not be after ${startTime}`
        } : {
          value: formatDateDisplay(endDate),
          defaultValue: `Date may not be after ${formatDateDisplay(endDate)}`
        });
      case 'start_too_soon':
        return t('video:broadcast_live.start_too_soon');
      // case 'start_too_far':
      //   return t('video:broadcast_live.start_too_far', {
      //     defaultValue: 'Start date/time cannot be more than 24 hours from now',
      //   });
      default:
        return '';
    }
  };

  const handleThumbnailSelect = (file: File | null) => {
    setThumbnailError(null);
    if (!file) { return; }
    if (!file.type.startsWith('image/')) {
      setThumbnailError(t('upload:thumbnail_picker.errors.invalid_file_type'));
      return;
    }
    if (file.size > THUMBNAIL_SIZE_LIMIT_MB * 1024 * 1024) {
      setThumbnailError(t('upload:thumbnail_picker.errors.file_size', { size: THUMBNAIL_SIZE_LIMIT_MB }));
      return;
    }
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleThumbnailDelete = () => {
    if (thumbnailPreview) { URL.revokeObjectURL(thumbnailPreview); }
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailError(null);
  };

  const handleTimeChange = (
    value: string,
    setTime: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    setError('');

    if (!value.trim()) {
      setTime('');
      return;
    }

    const normalized = normalizeTimeInput(value);

    if (normalized) {
      setTime(normalized);
      return;
    }

    setTime(value);
    setError(t('video:broadcast_live.valid_time'));
  };

  const isValid =
    !!title.trim() &&
    (!!thumbnailFile || !!thumbnailPreview) &&
    !!ingestRegion &&
    !!ingestFormat &&
    !!startDateTime &&
    !!endDateTime &&
    !startTimeError &&
    !endTimeError &&
    !scheduleError;

  const uploadThumbnail = async (file: File): Promise<string> => {
    const key = generateS3Key(user?.id || 'anonymous', file.name, 'broadcast-thumbnails');
    const { data } = await getPresignedUrl({ variables: { key } });
    const url = data?.getS3UploadPresignedUrl?.preSignedUrl;
    if (!url) { throw new Error('Failed to get upload URL'); }
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'image/jpeg' },
    });
    if (!response.ok) { throw new Error('Failed to upload thumbnail'); }
    return key;
  };

  const handleEdit = async () => {
    setSubmitted(true);
    setFormError(null);
    const hasValidThumbnail = !!thumbnailFile || !!thumbnailPreview;
    if (!isValid || !hasValidThumbnail || !startDateTime || !endDateTime || !streamId) { return; }

    try {
      setSubmitting(true);
      let thumbnailKey = '';
      if (thumbnailFile) {
        thumbnailKey = await uploadThumbnail(thumbnailFile);
      } else if (thumbnailPreview) {
        thumbnailKey = thumbnailPreview.replace(`${env.APP_BASE_URL || ''}`, '');
      }

      const input: EditBroadcastLiveInput = {
        blId: streamId,
        blTitle: title.trim(),
        ...(description.trim() ? { blDescription: description.trim() } : {}),
        ...(thumbnailKey ? { blThumbnailUrl: thumbnailKey } : {}),
        // Only send schedule fields the current status actually allows editing —
        // avoids overwriting the backend value with a stale/locked local state.
        ...(canEditStartTime ? { blStartTime: toIsoUtc(startDateTime) as string } : {}),
        ...(canEditEndTime ? { blEndTime: toIsoUtc(endDateTime) as string } : {}),
      };

      const result = await getAuthApolloClient().mutate<EditBroadcastLiveData, EditBroadcastLiveVariables>({
        mutation: EDIT_BROADCAST_LIVE,
        variables: { input },
        errorPolicy: 'all',
      });

      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message || 'Failed to update live stream');
      }

      if (!result.data?.editLiveStream) {
        throw new Error('Failed to update live stream');
      }


      showToast(result.data.editLiveStream, 'success');
      navigate(`${PATH_BROADCAST_LIVE_DETAILS}/${streamId}`, { replace: true });
    } catch (error) {
      let message = t('video:broadcast_live.create_error');
      if (error instanceof Error) {
        message = error.message;
      } else if (CombinedGraphQLErrors.is(error)) {
        message = error.errors[0]?.message || message;
      }
      setFormError(message);
      setSubmitting(false);
    }
  };

  const showError = (condition: boolean) => submitted && condition;

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('video:broadcast_live.edit_title')}</h2>

      {formError && <div className={styles.formError}>{formError}</div>}

      <div className={styles.scrollArea} data-scroll-container>
        <div className={styles.streamTypeRow}>
          <div className={styles.fieldLabel}>
            {t('video:broadcast_live.stream_type')}
            <span className={styles.required}>*</span>
          </div>
          <div className={styles.radioGroup}>
            <label className={classNames(styles.radio, { [styles.radioActive]: streamType === 'event' })}>
              <input
                type="radio"
                name="streamType"
                checked={streamType === 'event'}
                disabled={isFullyLocked}
                onChange={() => setStreamType('event')}
              />
              <span className={styles.radioMark} />
              {t('video:broadcast_live.live_event')}
            </label>
            <label className={classNames(styles.radio, styles.radioDisabled)}>
              <input type="radio" name="streamType" disabled checked={false} readOnly />
              <span className={styles.radioMark} />
              {t('video:broadcast_live.always_on')}
            </label>
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.topGrid}>
          <div className={styles.leftColumn}>
            <TextField
              name="title"
              label={t('video:broadcast_live.title_label')}
              value={title}
              onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
              placeholder={t('video:broadcast_live.title_placeholder')}
              required
              maxLength={100}
              error={showError(!title.trim())}
              helperText={showError(!title.trim()) ? t('upload:this_field_is_required') : ''}
              showOptionalLabel={false}
            />
            <TextField
              name="description"
              label={t('video:broadcast_live.description_label')}
              value={description}
              onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              placeholder={t('video:broadcast_live.description_placeholder')}
              multiline
              maxLength={500}
              showOptionalLabel={false}
            />
          </div>

          <div className={styles.rightColumn}>
            <div className={styles.thumbnailLabel}>
              {t('video:broadcast_live.thumbnail_label')}
              <span className={styles.thumbnailHint}>{t('video:broadcast_live.thumbnail_size')}</span>
              <span className={styles.required}>*</span>
            </div>
            <UploadBox
              type="image"
              accept="image/*"
              height={210}
              poster={thumbnailPreview || undefined}
              previewUrl={thumbnailPreview}
              selectedFile={thumbnailFile}
              onFileSelect={handleThumbnailSelect}
              onDelete={thumbnailPreview ? handleThumbnailDelete : undefined}
              title={t('video:broadcast_live.add_image')}
              hint={t('video:broadcast_live.thumbnail_max_size', { size: THUMBNAIL_SIZE_LIMIT_MB })}
              showControlsOnHover
              error={showError(!thumbnailFile) || !!thumbnailError}
              className={styles.thumbnailBox}
            />
            {thumbnailError && <div className={styles.fieldError}>{thumbnailError}</div>}
            {showError(!thumbnailFile && !thumbnailPreview) && !thumbnailError && (
              <div className={styles.fieldError}>{t('upload:this_field_is_required')}</div>
            )}
          </div>
        </div>

        <div className={styles.twoCol}>
          <Dropdown
            name="ingestRegion"
            label={t('video:broadcast_live.ingest_region')}
            value={ingestRegion}
            onChange={(e) => setIngestRegion(e.target.value)}
            options={regionOptions}
            defaultLabel={t('video:broadcast_live.select_region')}
            required
            fullWidth
            disabled={isFullyLocked}
            error={showError(!ingestRegion)}
            helperText={showError(!ingestRegion) ? t('upload:this_field_is_required') : ''}
          />
          <Dropdown
            name="ingestFormat"
            label={
              <span className={styles.labelWithTooltip}>
                {t('video:broadcast_live.ingest_format')}
                <Tooltip title={t('video:broadcast_live.ingest_format_tooltip')}>
                  <Icon icon={HelpIcon} className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            value={ingestFormat}
            onChange={(e) => setIngestFormat(e.target.value)}
            options={formatOptions}
            defaultLabel={t('video:broadcast_live.select_format')}
            required
            fullWidth
            disabled={isFullyLocked}
            error={showError(!ingestFormat)}
            helperText={showError(!ingestFormat) ? t('upload:this_field_is_required') : ''}
          />
        </div>

        <div className={styles.threeCol}>
          <PickerField
            label={t('video:broadcast_live.start_date')}
            required
            icon={CalendarIcon}
            popoverWidth={300}
            value={formatDateDisplay(startDate)}
            placeholder="MM/DD/YYYY"
            disabled={!canEditStartTime}
            error={showError(!startDate) || showError(scheduleError === 'start_too_soon')}
            helperText={
              showError(scheduleError === 'start_too_soon')
                ? getScheduleErrorMessage('start_too_soon')
                : showError(!startDate)
                  ? t('upload:this_field_is_required')
                  : ''
            }
          >
            {(close) => (
              <Calendar
                value={startDate}
                minDate={new Date()}
                onSelect={(date) => {
                  setStartDate(date);
                  setEndDate(date);
                  close();
                }}
              />
            )}
          </PickerField>

          <PickerField
            label={t('video:broadcast_live.local_time')}
            required
            icon={ClockIcon}
            popoverWidth={200}
            value={startTime}
            placeholder="12:00 AM"
            editable={canEditStartTime}
            disabled={!canEditStartTime}
            onValueChange={(val) => handleTimeChange(val, setStartTime, setStartTimeError)}
            error={showError(!startTime) || !!startTimeError}
            helperText={
              startTimeError ||
              (showError(!startTime) ? t('upload:this_field_is_required') : '')
            }
          >
            {(close) => (
              <TimeList
                value={startTime}
                onSelect={(value) => {
                  setStartTime(value);
                  setStartTimeError('');
                  close();
                }}
              />
            )}
          </PickerField>

          <div className={styles.utcField}>
            <div className={styles.fieldLabel}>{t('video:broadcast_live.utc')}</div>
            <div className={styles.utcValue}>{formatUtcDisplay(startDateTime) || '--'}</div>
          </div>
        </div>

        <div className={styles.threeCol}>
          <PickerField
            label={t('video:broadcast_live.end_date')}
            required
            icon={CalendarIcon}
            popoverWidth={300}
            value={formatDateDisplay(endDate)}
            placeholder="MM/DD/YYYY"
            disabled={!canEditEndTime}
            error={
              showError(!endDate) || scheduleDateError === 'max_duration' ||
              showError(scheduleDateError === 'end_before_start') ||
              showError(scheduleDateError === 'min_duration')
            }
            helperText={
              scheduleDateError && (submitted || scheduleDateError === 'max_duration' || scheduleDateError === 'end_before_start' || scheduleDateError === 'min_duration')
                ? getScheduleErrorMessage(scheduleDateError)
                : showError(!endDate)
                  ? t('upload:this_field_is_required')
                  : ''
            }
          >
            {(close) => (
              <Calendar
                value={endDate}
                minDate={startDate || new Date()}
                onSelect={(date) => {
                  setEndDate(date);
                  close();
                }}
              />
            )}
          </PickerField>

          <PickerField
            label={t('video:broadcast_live.local_time')}
            required
            icon={ClockIcon}
            popoverWidth={200}
            value={endTime}
            placeholder="12:00 AM"
            editable={canEditEndTime}
            disabled={!canEditEndTime}
            onValueChange={(val) => handleTimeChange(val, setEndTime, setEndTimeError)}
            error={
              showError(!endTime) ||
              !!endTimeError ||
              (!!scheduleTimeError && (submitted || scheduleTimeError === 'max_duration'))
            }
            helperText={
              endTimeError ||
              (scheduleTimeError && (submitted || scheduleTimeError === 'max_duration')
                ? getScheduleErrorMessage(scheduleTimeError, 'time')
                : showError(!endTime)
                  ? t('upload:this_field_is_required')
                  : ''
              )
            }
          >
            {(close) => (
              <TimeList
                value={endTime}
                onSelect={(value) => {
                  setEndTime(value);
                  setEndTimeError('');
                  close();
                }}
              />
            )}
          </PickerField>

          <div className={styles.utcField}>
            <div className={styles.fieldLabel}>{t('video:broadcast_live.utc')}</div>
            <div className={styles.utcValue}>{formatUtcDisplay(endDateTime) || '--'}</div>
            {maxEndDateTime && (
              <div className={styles.utcHint}>
                {t('video:broadcast_live.max_end_utc', { value: formatUtcDisplay(maxEndDateTime) })}
              </div>
            )}
          </div>
        </div>

        <div className={styles.twoCol}>
          <Dropdown
            name="contentType"
            label={
              <span className={styles.labelWithTooltip}>
                {t('video:broadcast_live.content_type')}
                <Tooltip title={t('video:broadcast_live.content_type_tooltip')}>
                  <Icon icon={HelpIcon} className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            options={contentTypeOptions}
            hideOptional
            fullWidth
            disabled={isFullyLocked}
          />
          <Dropdown
            name="dvrWindow"
            label={
              <span className={styles.labelWithTooltip}>
                {t('video:broadcast_live.dvr_window')}
                <Tooltip title={t('video:broadcast_live.dvr_window_tooltip')}>
                  <Icon icon={HelpIcon} className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            value={dvrWindow}
            onChange={(e) => setDvrWindow(e.target.value)}
            options={dvrOptions}
            hideOptional
            fullWidth
            disabled={isFullyLocked}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerNote}>
          <Icon icon={InfoIcon} className={styles.footerIcon} />
          {t('video:broadcast_live.processing_note')}
        </div>
        <Button
          label={t('video:broadcast_live.save_button')}
          variant="contained"
          color="primary"
          onClick={handleEdit}
          disabled={submitting}
          className={styles.createButton}
        />
      </div>

      {submitting && (
        <div className={styles.loadingOverlay}>
          <Spinner size="medium" />
        </div>
      )}
    </div>
  );
};

export default EditLiveStreamModal;